import { Input } from "@/components/ui/input";
import { handleValidation } from "@/utils/form";
import { formatNumberLocale } from "@/utils/helpers";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";

type Props = {
  amount: number;
  setAmount: Dispatch<SetStateAction<number>>;
  inputErrorText: string;
  setInputErrorText: Dispatch<SetStateAction<string>>;
  bankAccountIsVerified: boolean | undefined;
  balance: number;
}

function WithdrawalTransactionComponent(props: Props) {
  const {
    amount,
    setAmount,
    inputErrorText,
    setInputErrorText,
    bankAccountIsVerified,
    balance,
  } = props;

  const intl = useIntl();
  const { t } = useTranslation(["modals"]);

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    let errorText = "";

    const parsedValue = parseFloat(value);
    const isValid = handleValidation(value, "float");

    if (!isValid) {
      errorText = t("makeinvestment.input.error.invalid");
    } else if (parsedValue < 0) {
      errorText = t("makeinvestment.input.error.zero");
    } else if (parsedValue > balance) {
      errorText = t("makeinvestment.input.error.insufficient");
    }

    setInputErrorText(errorText);

    if (isValid || value.length === 0) {
      const finalValue = value.length === 0 ? 0 : parsedValue;
      setAmount(finalValue);
    }
  }

  return (
    <div>
      {
        bankAccountIsVerified &&
        <>
          <div className="mt-4">
            <p className="text-sm text-gray-400 text-center font-semibold dark:text-gray-100">
              {t("addtransaction.makewithdrawal")}
            </p>
            <div className="flex items-center mt-4">
              <p className="text-sm font-semibold w-[150px] text-gray-800 dark:text-white">
                {t("makeinvestment.availablebalance")}:
              </p>
              <p className="text-mforange font-semibold">
                {formatNumberLocale(intl, balance, "currency")}
              </p>
            </div>
          </div>
          <div className="flex w-full">
            <div className="bg-gray-100 dark:bg-mfdarklighter rounded-sm p-2 w-full">
              <div className="flex items-center">
                <p className="font-semibold text-4xl text-gray-500 dark:text-white pl-2">
                  MYEU
                </p>
                <div className="w-full">
                  <Input
                    id="investmentAmount"
                    onChange={handleOnChange}
                    type="number"
                    name="amount"
                    placeholder={"0.00"}
                    defaultValue={amount > 0 ? amount.toString() : ""}
                    className="ml-1 h-[36px] text-3xl bg-transparent md:text-3xl lg:text-4xl placeholder:text-3xl md:placeholder:text-3xl lg:placeholder:text-4xl font-bold dark:placeholder:text-gray-100 dark:text-gray-100 border-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    autoFocus
                  />
                </div>
              </div>
            </div>
          </div>
          {amount > 0 && inputErrorText.length === 0 &&
            <p className="text-sm text-gray-400 dark:text-gray-100 pt-1">
              {t("addtransaction.receive")} &nbsp;
              {formatNumberLocale(intl, amount, "currency")}
            </p>
          }
          {
            inputErrorText.length > 0 &&
            <p className="h-4 text-xs text-red-600 mt-1">{inputErrorText}</p>
          }
        </>
      }
      {
        !bankAccountIsVerified &&
          <div className="mt-8 select-none dark:text-white">
            <p className="text-lg font-semibold mb-4">
              {t("addtransaction.notverified")}
            </p>
            <p className="mb-2">
              {t("addtransaction.contactmyronbank")}
            </p>
            <p className="">
              {t("addtransaction.contact")} &nbsp;
              <a href="mailto:contact@myron.finance" className="text-blue-400">
                contact@myron.finance
              </a>
            </p>
          </div>
      }
    </div>
  );
}

export default WithdrawalTransactionComponent;
